class MainSearchLive extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[name="q"]');
    this.form = this.querySelector("form");
    this.section = this.closest("[data-section-id]");
    this.sectionId = this.section?.dataset.sectionId;
    this.productGridContainer =
      this.section?.querySelector("#ProductGridContainer");

    this.abortController = null;
    this.requestId = 0;
    this.cache = new Map();

    this.onInput = this.onInput.bind(this);

    this.debouncedSearch = this.debounce(() => {
      this.performLiveSearch();
    }, 300);
  }

  connectedCallback() {
    if (
      !this.input ||
      !this.section ||
      !this.sectionId ||
      !this.productGridContainer
    ) {
      console.warn(
        "MainSearchLive could not initialise because required elements were not found.",
      );

      return;
    }

    this.input.addEventListener(
      "input",
      this.onInput,
    );
  }

  disconnectedCallback() {
    this.input?.removeEventListener(
      "input",
      this.onInput,
    );

    this.abortController?.abort();
  }

  onInput() {
    this.debouncedSearch();
  }

  getSearchTerm() {
    return this.input?.value.trim() || "";
  }

  async performLiveSearch() {
    const searchTerm = this.getSearchTerm();

    if (!searchTerm) {
      this.abortController?.abort();
      this.requestId += 1;
      this.setLoading(false);
      return;
    }

    const cacheKey =
      searchTerm.toLowerCase();

    if (this.cache.has(cacheKey)) {
      this.renderResult(
        this.cache.get(cacheKey),
        searchTerm,
      );

      return;
    }

    this.abortController?.abort();
    this.abortController =
      new AbortController();

    const signal =
      this.abortController.signal;

    const currentRequestId =
      ++this.requestId;

    this.setLoading(true);

    try {
      /*
       * First request the real predictive product results.
       */
      const predictiveResult =
        await this.fetchPredictiveResults(
          searchTerm,
          signal,
        );

      if (
        signal.aborted ||
        currentRequestId !== this.requestId ||
        searchTerm !== this.getSearchTerm()
      ) {
        return;
      }

      /*
       * Display predictive products when Shopify returns any.
       */
      if (
        predictiveResult.productCount > 0
      ) {
        const result = {
          source: "predictive",
          productCount:
            predictiveResult.productCount,
          productGridHtml:
            predictiveResult.productGridHtml,
          completeContainerHtml: null,
          titleHtml: null,
        };

        this.cache.set(
          cacheKey,
          result,
        );

        this.renderResult(
          result,
          searchTerm,
        );

        return;
      }

      /*
       * Predictive search returned zero products.
       * Now use normal storefront search.
       */
      const storefrontResult =
        await this.fetchStorefrontResults(
          searchTerm,
          signal,
        );

      if (
        signal.aborted ||
        currentRequestId !== this.requestId ||
        searchTerm !== this.getSearchTerm()
      ) {
        return;
      }

      this.cache.set(
        cacheKey,
        storefrontResult,
      );

      this.renderResult(
        storefrontResult,
        searchTerm,
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(
          "Live main search failed:",
          error,
        );
      }
    } finally {
      if (
        currentRequestId === this.requestId
      ) {
        this.abortController = null;
        this.setLoading(false);
      }
    }
  }

  async fetchPredictiveResults(
    searchTerm,
    signal,
  ) {
    const predictiveSearchUrl =
      window.routes?.predictive_search_url ||
      `${window.Shopify?.routes?.root || "/"}search/suggest`;

    const parameters =
      new URLSearchParams({
        q: searchTerm,
        "resources[type]": "product",
        "resources[limit]": "10",
        "resources[limit_scope]": "each",
        "resources[options][unavailable_products]":
          "last",
        "resources[options][fields]":
          "title,product_type,variants.title,variants.sku",
        section_id: this.sectionId,
      });

    const response = await fetch(
      `${predictiveSearchUrl}?${parameters.toString()}`,
      {
        signal,
        headers: {
          "X-Requested-With":
            "XMLHttpRequest",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Predictive search failed with status ${response.status}.`,
      );
    }

    const responseText =
      await response.text();

    const document =
      new DOMParser().parseFromString(
        responseText,
        "text/html",
      );

    const predictiveResults =
      document.querySelector(
        "#MainSearchPredictiveResults",
      );

    if (!predictiveResults) {
      throw new Error(
        "Predictive search response did not contain #MainSearchPredictiveResults.",
      );
    }

    const productCount =
      Number.parseInt(
        predictiveResults.dataset
          .predictiveProductCount || "0",
        10,
      ) || 0;

    const productGrid =
      predictiveResults.querySelector(
        "#product-grid",
      );

    return {
      productCount,
      productGridHtml:
        productGrid?.outerHTML || "",
    };
  }

  async fetchStorefrontResults(
    searchTerm,
    signal,
  ) {
    const searchUrl =
      window.routes?.search_url ||
      `${window.Shopify?.routes?.root || "/"}search`;

    const parameters =
      new URLSearchParams({
        q: searchTerm,
        type: "product",
        "options[prefix]": "last",
        section_id: this.sectionId,
      });

    const response = await fetch(
      `${searchUrl}?${parameters.toString()}`,
      {
        signal,
        headers: {
          "X-Requested-With":
            "XMLHttpRequest",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Storefront search failed with status ${response.status}.`,
      );
    }

    const responseText =
      await response.text();

    const document =
      new DOMParser().parseFromString(
        responseText,
        "text/html",
      );

    const returnedSection =
      document.querySelector(
        `[data-section-id="${CSS.escape(
          this.sectionId,
        )}"]`,
      );

    const newProductGridContainer =
      returnedSection?.querySelector(
        "#ProductGridContainer",
      );

    if (!newProductGridContainer) {
      throw new Error(
        "Storefront response did not contain #ProductGridContainer.",
      );
    }

    const returnedTitle =
      returnedSection.querySelector(
        ".search-results__title",
      );

    return {
      source: "storefront",
      productCount: null,
      productGridHtml: null,
      completeContainerHtml:
        newProductGridContainer.innerHTML,
      titleHtml:
        returnedTitle?.innerHTML || null,
    };
  }

  renderResult(
    result,
    searchTerm,
  ) {
    if (result.source === "predictive") {
      this.renderPredictiveResult(
        result,
        searchTerm,
      );
    } else {
      this.renderStorefrontResult(
        result,
      );
    }

    this.updateUrl(searchTerm);
    this.dispatchUpdateEvent(
      result.source,
      searchTerm,
    );
  }

  renderPredictiveResult(
    result,
    searchTerm,
  ) {
    const tabContainer =
      this.productGridContainer.querySelector(
        ".main-search-tab__container",
      );

    if (!tabContainer) {
      console.error(
        "Could not find .main-search-tab__container.",
      );

      return;
    }

    /*
     * Predictive HTML uses the same:
     * - collection grid classes
     * - collection item classes
     * - card snippet
     * - image settings
     * - badges
     * - prices
     * - rating settings
     */
    tabContainer.innerHTML =
      result.productGridHtml;

    const currentTitle =
      this.section.querySelector(
        ".search-results__title",
      );

    if (currentTitle) {
      currentTitle.innerHTML = `
        Search:
        <em class="search-results__terms">${this.escapeHtml(
          searchTerm,
        )}</em>
      `;
    }
  }

  renderStorefrontResult(result) {
    this.productGridContainer.innerHTML =
      result.completeContainerHtml;

    if (result.titleHtml) {
      const currentTitle =
        this.section.querySelector(
          ".search-results__title",
        );

      if (currentTitle) {
        currentTitle.innerHTML =
          result.titleHtml;
      }
    }
  }

  updateUrl(searchTerm) {
    const searchUrl =
      window.routes?.search_url ||
      `${window.Shopify?.routes?.root || "/"}search`;

    const url = new URL(
      searchUrl,
      window.location.origin,
    );

    url.searchParams.set(
      "q",
      searchTerm,
    );

    url.searchParams.set(
      "type",
      "product",
    );

    url.searchParams.set(
      "options[prefix]",
      "last",
    );

    window.history.replaceState(
      {},
      "",
      url.toString(),
    );
  }

  dispatchUpdateEvent(
    source,
    searchTerm,
  ) {
    document.dispatchEvent(
      new CustomEvent(
        "main-search:updated",
        {
          detail: {
            sectionId: this.sectionId,
            source,
            searchTerm,
          },
        },
      ),
    );
  }

  setLoading(isLoading) {
    this.toggleAttribute(
      "loading",
      isLoading,
    );

    this.productGridContainer?.setAttribute(
      "aria-busy",
      isLoading
        ? "true"
        : "false",
    );
  }

  escapeHtml(value) {
    const element =
      document.createElement("div");

    element.textContent = value;

    return element.innerHTML;
  }

  debounce(callback, delay) {
    let timeoutId;

    return (...args) => {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(
        () => {
          callback.apply(this, args);
        },
        delay,
      );
    };
  }
}

if (
  !customElements.get(
    "main-search-live",
  )
) {
  customElements.define(
    "main-search-live",
    MainSearchLive,
  );
}